import { useEffect, useId, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useCarrier } from '../../contexts/CarrierContext';
import './CarrierSwitcher.css';

const ancestors = (node) => {
  const parents = [];
  for (let parent = node.parentElement; parent; parent = parent.parentElement) parents.push(parent);
  return parents;
};

// Carry focus across CarrierScope remounts without overriding a guard or panel.
const focusRequests = new WeakMap();

const normalize = (value) => value.toLocaleLowerCase('fa').replace(/ي/g, 'ی').replace(/ك/g, 'ک');

export default function CarrierSwitcher({ variant = 'bar' }) {
  const { inShell, carriers = [], carrier, loading, switchTo } = useCarrier();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeSlug, setActiveSlug] = useState(null);
  const [keyboard, setKeyboard] = useState(true);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const selecting = useRef(false);
  const typeahead = useRef({ text: '', time: 0 });
  const id = useId();
  const hasSearch = carriers.length > 8;
  const filtered = carriers.filter((item) => normalize(`${item.brand_name} ${item.slug}`).includes(normalize(query.trim())));
  const activeIndex = filtered.findIndex((item) => item.slug === activeSlug);
  const optionId = (slug) => `${id}-option-${slug}`;
  const activeId = activeIndex < 0 ? undefined : optionId(activeSlug);

  const changeOpen = (next) => {
    setOpen(next);
    if (next) {
      setQuery('');
      setActiveSlug(carrier?.slug ?? carriers[0]?.slug ?? null);
      setKeyboard(true);
      selecting.current = false;
      typeahead.current = { text: '', time: 0 };
    }
  };

  useEffect(() => {
    if (loading) setOpen(false);
  }, [loading]);

  useEffect(() => {
    if (!open || !keyboard || !activeId) return;
    // Popper measures the available height after mounting the portal. Wait for
    // that layout before revealing a current carrier below the first page.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, keyboard, activeId]);

  useEffect(() => {
    const trigger = triggerRef.current;
    const ownerDocument = trigger?.ownerDocument;
    if (!ownerDocument || focusRequests.get(ownerDocument) !== carrier?.slug) return;
    focusRequests.delete(ownerDocument);
    const timer = setTimeout(() => {
      if (trigger.isConnected && ownerDocument.activeElement === ownerDocument.body) trigger.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [carrier?.slug]);

  const commit = (next) => {
    selecting.current = true;
    setOpen(false);
    // Restore focus before the asynchronous leave guard can open its dialog.
    triggerRef.current?.focus();
    if (next === carrier?.slug) return;
    const ownerDocument = triggerRef.current?.ownerDocument;
    focusRequests.set(ownerDocument, next);
    Promise.resolve(switchTo(next)).then((switched) => {
      if (!switched && focusRequests.get(ownerDocument) === next) focusRequests.delete(ownerDocument);
    });
  };

  const onKeyDown = (event) => {
    if (event.key === 'Tab') {
      selecting.current = true;
      setOpen(false);
      event.preventDefault();
      // The panel is portalled, so Tab must continue from the trigger's position.
      const trigger = triggerRef.current;
      const controls = [...trigger.ownerDocument.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter((node) => node.tabIndex >= 0 && !node.matches(':disabled, [data-radix-focus-guard]')
          && !node.closest('.carrier-picker, [hidden], [inert]')
          && ![node, ...ancestors(node)].some((parent) => {
            const style = getComputedStyle(parent);
            return style.display === 'none' || style.visibility === 'hidden';
          }));
      const index = controls.indexOf(trigger) + (event.shiftKey ? -1 : 1);
      (controls[index] || trigger).focus();
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      setKeyboard(true);
      const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? filtered.length - 1
        : Math.max(0, Math.min(filtered.length - 1, activeIndex + (event.key === 'ArrowDown' ? 1 : -1)));
      setActiveSlug(filtered[nextIndex]?.slug ?? null);
    } else if (event.key === 'Enter' || (event.key === ' ' && event.target === listRef.current)) {
      event.preventDefault();
      if (activeIndex >= 0) commit(filtered[activeIndex].slug);
    } else if (event.target === listRef.current && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const text = (now - typeahead.current.time < 700 ? typeahead.current.text : '') + normalize(event.key);
      typeahead.current = { text, time: now };
      const prefix = [...text].every((letter) => letter === text[0]) ? text[0] : text;
      const candidates = [...filtered.slice(activeIndex + 1), ...filtered.slice(0, activeIndex + 1)];
      const next = candidates.find((item) => normalize(item.brand_name).startsWith(prefix));
      if (next) { setKeyboard(true); setActiveSlug(next.slug); }
    }
  };

  if (!inShell || (!carrier && !loading)) return null;
  return <Popover.Root open={open && !loading} onOpenChange={changeOpen}>
    <div className={`carrier-switch carrier-switch-${variant}`} dir="rtl">
      {variant === 'dark' && <span className="carrier-switch-label">اپراتور</span>}
      <Popover.Trigger asChild>
        <button type="button" ref={triggerRef} className="carrier-switch-trigger"
          aria-label="اپراتور فعال" aria-describedby={`${id}-name`}
          aria-haspopup="listbox" aria-expanded={open && !loading} aria-controls={open ? `${id}-list` : undefined}
          aria-busy={loading || undefined} disabled={loading}
          onKeyDown={(event) => {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
              event.preventDefault();
              if (!open) changeOpen(true);
            }
          }}>
          <span className="carrier-switch-swatch" aria-hidden="true" style={{ backgroundColor: carrier?.map_color || '#94a3b8' }} />
          {variant === 'bar' && <span className="carrier-switch-prefix">اپراتور:</span>}
          <bdi className="carrier-switch-name" id={`${id}-name`}>{loading ? '…' : carrier?.brand_name}</bdi>
          <ChevronDown className="carrier-switch-chevron" size={14} aria-hidden="true" />
        </button>
      </Popover.Trigger>
    </div>
    <Popover.Portal>
      <Popover.Content className="carrier-picker" role="presentation" dir="rtl" align="start" side="bottom" sideOffset={6} collisionPadding={12}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (hasSearch ? searchRef : listRef).current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          if (selecting.current) event.preventDefault();
        }}
        onKeyDown={onKeyDown}>
        {hasSearch && <div className="carrier-picker-search">
          <Search size={16} aria-hidden="true" />
          <input ref={searchRef} type="search" aria-label="جست‌وجوی اپراتور" placeholder="جست‌وجوی اپراتور"
            role="combobox" aria-autocomplete="list" aria-expanded="true" aria-controls={`${id}-list`} aria-activedescendant={activeId}
            value={query} onChange={(event) => {
              const value = event.target.value;
              setQuery(value);
              const results = carriers.filter((item) => normalize(`${item.brand_name} ${item.slug}`).includes(normalize(value.trim())));
              setActiveSlug(results.find((item) => item.slug === carrier?.slug)?.slug ?? results[0]?.slug ?? null);
              setKeyboard(true);
            }} />
        </div>}
        <div ref={listRef} id={`${id}-list`} role="listbox" aria-label="اپراتورها" aria-activedescendant={activeId}
          tabIndex={0} className="carrier-picker-list">
          {filtered.map((item) => <div key={item.id} id={optionId(item.slug)} role="option"
            aria-selected={item.slug === carrier?.slug} className="carrier-picker-option"
            data-active={keyboard && item.slug === activeSlug ? '' : undefined}
            onPointerMove={() => { setKeyboard(false); setActiveSlug(item.slug); }}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => commit(item.slug)}>
            <span className="carrier-switch-swatch" aria-hidden="true" style={{ backgroundColor: item.map_color || '#94a3b8' }} />
            <bdi className="carrier-picker-name">{item.brand_name}</bdi>
            {item.status === 'inactive' && <span className="carrier-picker-inactive">غیرفعال</span>}
            <span className="carrier-picker-check" aria-hidden="true">{item.slug === carrier?.slug && <Check size={16} />}</span>
          </div>)}
        </div>
        {filtered.length === 0 && <p className="carrier-picker-empty" role="status">اپراتوری پیدا نشد</p>}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>;
}
